import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Banknote, Smartphone, Landmark, Check } from "lucide-react";
import { StoreShell } from "@/components/store/StoreShell";
import { OtpLogin } from "@/components/store/OtpLogin";
import { PolicyNote } from "@/components/store/PolicyNote";
import { FeeLines, MinOrderNotice, DeliveryRules } from "@/components/store/DeliveryRules";
import { BankDetails, UpiQr } from "@/components/store/BankDetails";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useStore, lineTid } from "@/context/StoreContext";
import { api, errMsg, inr } from "@/lib/api";

const PAYMENTS = [
  { id: "cod", label: "Cash on delivery", desc: "Pay cash to our rider", icon: Banknote },
  { id: "upi_delivery", label: "UPI at delivery", desc: "Scan & pay when your order arrives", icon: Smartphone },
  { id: "bank_transfer", label: "Bank transfer", desc: "Pay now, share UTR/screenshot. Packed after we verify.", icon: Landmark },
];

const Step = ({ n, title, done, children, testId }) => (
  <section className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-7" data-testid={testId}>
    <div className="flex items-center gap-3 mb-5">
      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${done ? "bg-secondary text-white" : "bg-primary text-white"}`}>{done ? <Check className="w-4 h-4" /> : n}</span>
      <h2 className="font-display text-2xl sm:text-3xl text-primary font-semibold">{title}</h2>
    </div>
    {children}
  </section>
);

const Tile = ({ active, onClick, disabled, children, testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`text-left rounded-xl border-2 p-4 transition-colors disabled:opacity-45 disabled:cursor-not-allowed ${active ? "border-primary bg-primary/5" : "border-stone-200 hover:border-primary/40"}`}>
    {children}
  </button>
);

export default function Checkout() {
  const { cart, total, shortBy, customer, area, setAreaOpen, config, applyServerItems, refreshCart, clear } = useStore();
  const nav = useNavigate();
  const [slots, setSlots] = useState([]);
  const [slotId, setSlotId] = useState("");
  const [pay, setPay] = useState("cod");
  const [utr, setUtr] = useState("");
  const [form, setForm] = useState({ name: "", line1: "", landmark: "", note: "" });
  const [agree, setAgree] = useState(false);
  const [waUpdates, setWaUpdates] = useState(true);
  const [trusted, setTrusted] = useState(true);
  useEffect(() => { if (customer) api.get("/customer/me").then((r) => setTrusted(!!r.data.cod_trusted)).catch(() => {}); }, [customer]);
  const [busy, setBusy] = useState(false);
  const idem = useRef(crypto.randomUUID());

  const needsPrep = cart.some((i) => i.needs_prep);
  const loadSlots = () => api.get("/slots/today", { params: { prep: needsPrep } }).then((r) => setSlots(r.data));
  useEffect(() => { loadSlots(); }, [needsPrep]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { refreshCart().catch(() => {}); }, [refreshCart]);
  useEffect(() => {
    if (customer) setForm((f) => ({ ...f, name: f.name || customer.name || "", line1: f.line1 || customer.address || "", landmark: f.landmark || customer.landmark || "" }));
  }, [customer]);

  if (!cart.length) {
    return (
      <StoreShell>
        <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="checkout-empty">
          <h1 className="font-display text-4xl text-primary">Your basket is empty</h1>
          <Link to="/" className="inline-block mt-6 h-12 leading-[3rem] px-8 rounded-full bg-primary text-white font-bold" data-testid="checkout-back-shop">Browse today's harvest</Link>
        </div>
      </StoreShell>
    );
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const ready = customer && area && slotId && form.name.trim().length >= 2 && form.line1.trim().length >= 5 && agree && config?.ordering_open && shortBy <= 0;

  const place = async () => {
    setBusy(true);
    try {
      const r = await api.post("/orders", {
        idempotency_key: idem.current, items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, price: i.price, cut: i.cut || "" })),
        slot_id: slotId, pincode: area.pincode, name: form.name, address: { line1: form.line1, landmark: form.landmark },
        payment_method: pay, payment_ref: pay === "bank_transfer" ? utr : "", customer_note: form.note, whatsapp_updates: waUpdates,
      });
      clear();
      idem.current = crypto.randomUUID();
      toast.success("Order placed!");
      nav(`/orders/${r.data.order.id}`, { replace: true });
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "cart_changed") { applyServerItems(d.items); idem.current = crypto.randomUUID(); }
      if (d?.code === "slot_unavailable") { setSlotId(""); loadSlots(); idem.current = crypto.randomUUID(); }
      if (d?.code === "area_unserviceable") setAreaOpen(true);
      if (d?.code === "below_minimum") idem.current = crypto.randomUUID();
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <StoreShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12 grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-10 items-start">
        <div className="space-y-5">
          <h1 className="font-display text-4xl sm:text-5xl text-primary font-semibold">Checkout</h1>
          <Step n={1} title="Verify your phone" done={!!customer} testId="checkout-step-phone">
            {customer ? <p className="text-stone-600" data-testid="verified-phone">Verified as <b className="text-primary">+91 {customer.phone}</b></p> : <OtpLogin />}
          </Step>

          <Step n={2} title="Delivery address" done={form.line1.trim().length >= 5 && form.name.trim().length >= 2} testId="checkout-step-address">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Full name" value={form.name} onChange={set("name")} className="h-12 rounded-xl" data-testid="checkout-name-input" />
              <button type="button" onClick={() => setAreaOpen(true)} className="h-12 rounded-xl border border-stone-200 px-4 text-left text-sm hover:border-primary/40 transition-colors" data-testid="checkout-change-pincode">
                <span className="text-stone-500">Pincode </span><b className="text-primary">{area?.pincode}</b> <span className="text-stone-500">· {area?.name}</span> <span className="text-xs font-semibold text-primary underline">Change area</span> <DeliveryRules area={area} />
              </button>
              <Textarea placeholder="House / flat, society, street" value={form.line1} onChange={set("line1")} className="sm:col-span-2 rounded-xl min-h-[80px]" data-testid="checkout-address-input" />
              <Input placeholder="Landmark (optional)" value={form.landmark} onChange={set("landmark")} className="h-12 rounded-xl" data-testid="checkout-landmark-input" />
              <Input placeholder="Note for rider (optional)" value={form.note} onChange={set("note")} className="h-12 rounded-xl" data-testid="checkout-note-input" />
            </div>
          </Step>

          <Step n={3} title={needsPrep ? "Delivery slot · Tomorrow" : "Same-day slot"} done={!!slotId} testId="checkout-step-slot">
            {needsPrep && (
              <p className="mb-4 text-sm rounded-xl bg-secondary/15 text-[#4f5d27] font-semibold px-4 py-3" data-testid="prep-time-note">
                Your basket has cut / chopped items or combos. We cut them fresh, so this whole order is delivered tomorrow ({slots[0]?.date}). Remove those items for same-day delivery.
              </p>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              {slots.map((s) => (
                <Tile key={s.id} active={slotId === s.id} disabled={!s.available} onClick={() => setSlotId(s.id)} testId={`slot-option-${s.id}`}>
                  <p className="font-bold text-primary">{s.label} · {s.start}–{s.end}</p>
                  <p className={`text-xs mt-1 ${s.available ? "text-stone-500" : "text-accent font-semibold"}`}>
                    {s.available ? (needsPrep ? `Tomorrow · ${s.remaining} spots left` : `Order by ${s.cutoff} · ${s.remaining} spots left`) : s.reason === "Full" ? "Slot full" : `Closed · cutoff was ${s.cutoff}`}
                  </p>
                </Tile>
              ))}
            </div>
            {slots.length > 0 && !slots.some((s) => s.available) && (
              <p className="mt-4 text-sm text-accent font-semibold" data-testid="no-slots-msg">All of today's slots are closed or full. Please check back tomorrow morning.</p>
            )}
          </Step>

          <Step n={4} title="Payment" done testId="checkout-step-payment">
            <div className="grid sm:grid-cols-3 gap-3">
              {PAYMENTS.map(({ id, label, desc, icon: Icon }) => (
                <Tile key={id} active={pay === id} onClick={() => setPay(id)} testId={`payment-option-${id}`}>
                  <Icon className="w-5 h-5 text-secondary mb-2" strokeWidth={1.5} />
                  <p className="font-bold text-primary text-sm">{label}</p>
                  <p className="text-xs text-stone-500 mt-1">{desc}</p>
                </Tile>
              ))}
            </div>
            {pay === "cod" && customer && !trusted && (
              <p className="mt-4 text-sm rounded-xl bg-accent/10 text-accent font-semibold px-4 py-3" data-testid="cod-approval-note">
                First cash-on-delivery order: we'll give you a quick call to confirm before packing. After your first delivery, COD orders go straight through.
              </p>
            )}
            {pay === "upi_delivery" && (
              <div className="mt-4 space-y-2" data-testid="upi-delivery-panel">
                <p className="text-sm text-stone-600">Pay when your order arrives — scan this QR or show it to the rider. Pay the final amount after any unavailable items are removed.</p>
                <UpiQr amount={total} />
              </div>
            )}
            {pay === "bank_transfer" && (
              <div className="mt-4 space-y-3" data-testid="bank-transfer-panel">
                <p className="text-sm text-stone-600">Transfer <b className="text-primary">{inr(total)}</b> to the account below. Your order is packed only after we verify the payment.</p>
                <BankDetails amount={total} />
                <Input placeholder="UTR / reference number (you can also add it after placing)" value={utr} onChange={(e) => setUtr(e.target.value)} className="h-12 rounded-xl" data-testid="checkout-utr-input" />
                <p className="text-xs text-stone-500">You can upload a payment screenshot on the order page after placing the order.</p>
              </div>
            )}
          </Step>
        </div>

        <aside className="lg:sticky lg:top-24 bg-white rounded-2xl border border-stone-100 shadow-sm p-5 sm:p-6 space-y-4" data-testid="checkout-summary">
          <h3 className="font-display text-2xl text-primary font-semibold">Order summary</h3>
          <div className="space-y-2 max-h-64 overflow-auto">
            {cart.map((i) => (
              <div key={i.key} className="flex justify-between text-sm" data-testid={`summary-line-${lineTid(i)}`}>
                <span className="text-stone-600">{i.name}{i.cut ? ` (${i.cut})` : ""} <span className="text-stone-400">× {i.qty}</span></span>
                <span className="font-semibold">{inr(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-stone-100 pt-3" data-testid="checkout-total"><FeeLines /></div>
          <MinOrderNotice />
          <PolicyNote />
          <label className="flex gap-3 items-start text-sm cursor-pointer" data-testid="policy-agree-label">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="policy-agree-checkbox" />
            <span>I understand unavailable items will be removed (not substituted) and refunded the same day.</span>
          </label>
          <label className="flex gap-3 items-start text-sm cursor-pointer" data-testid="whatsapp-updates-label">
            <Checkbox checked={waUpdates} onCheckedChange={(v) => setWaUpdates(!!v)} className="mt-0.5" data-testid="whatsapp-updates-checkbox" />
            <span>Send me order updates on WhatsApp (confirmed, out for delivery, delivered).</span>
          </label>
          {config && !config.ordering_open && <p className="text-sm text-accent font-semibold" data-testid="checkout-closed-msg">{config.closed_reason}</p>}
          <button disabled={!ready || busy} onClick={place} data-testid="place-order-btn"
            className="w-full h-14 rounded-full bg-primary text-white text-base font-bold disabled:opacity-40 active:scale-95 transition-transform">
            {busy ? "Placing order…" : `Place order · ${inr(total)}`}
          </button>
          {!customer && <p className="text-xs text-stone-500 text-center">Verify your phone to place the order.</p>}
        </aside>
      </div>
    </StoreShell>
  );
}
